/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useEffect, useState } from 'react';
import {
  EntityStatus,
  StepsFilter,
  TrackingFamily,
} from '@facephi/inphinite-common';
import {
  Chips,
  FilterMenu,
  FilterMenuContent,
  FilterMenuItem,
  FlexContainer,
  ButtonIcon,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { OptionsType, TableOptions } from './TableOptions';
import { useOperations } from '../../providers';
import { OrderBy } from '../../state/constants';

type FilterType = Record<string, string[] | undefined>;
export type FiltersType = OptionsType & FilterType;

type Props = {
  variables?: FiltersType;
  onChange?(value: FiltersType): void;
  children?: ReactNode;
  onDownload?(): Promise<void>;
  onChangeFilter(value: any): void;
  filter: any;
};

type DateRange = {
  from: Date | null;
  to: Date | null;
};

const LABEL: Record<string, string> = {
  family: 'Type',
  status: 'Status',
  currentStep: 'Step',
};

const options = {
  family: Object.values(TrackingFamily).map((value: string) => {
    return { label: value };
  }),
  status: Object.values(EntityStatus).map((value: string) => ({
    label: value,
  })),
  currentStep: Object.values(StepsFilter).map((value: string) => ({
    label: value,
  })),
};

export const TableOptionsOperations = ({
  variables = {},
  onChange,
  children,
  onDownload,
  onChangeFilter,
  filter,
}: Props) => {
  const [optionFilters, setOptionFilters] = useState<OptionsType>(variables);
  const [inputKey, setInputKey] = useState(Math.random());
  const [date, setDate] = useState<DateRange | undefined>(
    variables?.fromTimestamp && variables?.toTimestamp
      ? {
          from: new Date(variables?.fromTimestamp),
          to: new Date(variables?.toTimestamp),
        }
      : undefined,
  );
  const [text, setText] = useState<string | undefined>(variables?.text);

  const { t } = useTranslation();
  const { initialFilter } = useOperations();

  const handleFilter = (header: string, value: string) => {
    onChangeFilter((filter: any) => ({
      ...filter,
      [header]: filter[header]?.includes(value)
        ? filter[header]?.filter((key: any) => key !== value)
        : [...(filter[header] || []), value],
    }));
  };

  useEffect(() => {
    if (Object.keys(optionFilters).length) {
      onChange &&
        onChange({
          ...optionFilters,
          ...Object.entries(filter as FilterType).reduce(
            (acc, [key, values]) => ({
              ...acc,
              [key]: values?.length ? values : undefined,
            }),
            {},
          ),
        } as FiltersType);
    }
  }, [filter, optionFilters]);

  const resetFilters = () => {
    setOptionFilters({
      limit: 100,
      text: undefined,
      fromTimestamp: undefined,
      toTimestamp: undefined,
      orderBy: OrderBy.lastUpdateTime,
    });
    onChangeFilter(initialFilter);
    setInputKey(Math.random());
    setText(undefined);
    setDate(undefined);
  };

  return (
    <TableOptions
      values={variables}
      onChange={setOptionFilters}
      children={children}
      onDownload={onDownload}
      key={inputKey}
      text={text}
      setText={setText}
      date={date}
      setDate={setDate}
      filterComponent={
        <>
          <FilterMenu testId="filters-options" tooltip={t('Filter')}>
            {Object.entries(options).map(([header, itemOption], index) => {
              return (
                <React.Fragment key={index}>
                  <FilterMenuContent title={t(`${LABEL[header]}`)}>
                    {itemOption.map((value: { label: string }, count) => (
                      <FilterMenuItem
                        key={count}
                        testId={`filter-${value.label}`}
                        onChange={() => handleFilter(header, value.label)}
                        checked={filter[header]?.includes(value.label)}
                      >
                        {t(value.label.replaceAll('_', ' '))}
                      </FilterMenuItem>
                    ))}
                  </FilterMenuContent>
                </React.Fragment>
              );
            })}
          </FilterMenu>
          <ButtonIcon
            iconName="Trash"
            onClick={resetFilters}
            disabled={
              !variables.text &&
              !variables.fromTimestamp &&
              !variables.toTimestamp &&
              (!filter ||
                (filter &&
                  Object.entries(filter as FilterType).every(
                    ([header, values]) => (values || []).length === 0,
                  )))
            }
            variant="text"
          />
          <FlexContainer
            alignItems="center"
            columnGap="1.6"
            rowGap="1.6"
            wrap="wrap"
            testId="filter-menu-options"
            as="ul"
          >
            {Object.entries(filter as FilterType).map(
              ([header, values]) =>
                values?.map((label, index) => (
                  <li data-test={`labelBase-${label}`}>
                    <Chips
                      key={index}
                      label={t(`${label.replaceAll('_', ' ')}`)}
                      onClick={() => handleFilter(header, label)}
                      closeable
                    />
                  </li>
                )),
            )}
          </FlexContainer>
        </>
      }
    ></TableOptions>
  );
};
